import axios from '../axiosInstance';
import qs from 'qs';

/**
 * DataGrid-style query for enabled commodities (keyset pagination, sort, filter).
 * Backend: POST api/Commodities/queryEnabled
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledCommodities({
  search,
  pageSize,
  cursor,
  sortBy,
  sortDir,
  filterModel,
  accountIds = null,
  signal = null,
} = {}) {
  const body = {
    search: search ?? null,
    pageSize,
    cursor: cursor ?? null,
    sortBy: sortBy ?? 'name',
    sortDir: sortDir ?? 'asc',
    filterModel: filterModel ?? { items: [], logicOperator: 'and' },
  };

  const config = { signal };
  if (Array.isArray(accountIds) && accountIds.length) {
    config.params = { accountIds };
    config.paramsSerializer = (params) => qs.stringify(params, { arrayFormat: 'repeat' });
  }

  const { data } = await axios.post('/Commodities/queryEnabled', body, config);
  return data;
}

export async function getEnabledCommodities({
  search = '',
  pageSize = 50,
  lastId = null,
  transTypeId = null,
  accountIds = null,   // array of IDs
  signal = null,
} = {}) {

  // Build params so arrays serialize as repeated keys: accountIds=1&accountIds=2
  const params = new URLSearchParams();
  if (search != null) params.set('search', search);
  if (pageSize != null) params.set('pageSize', String(pageSize));
  if (lastId != null) params.set('lastId', String(lastId));
  if (transTypeId != null) params.set('transTypeId', String(transTypeId));
  if (Array.isArray(accountIds) && accountIds.length) {
    accountIds.forEach(id => params.append('accountIds', String(id)));
  }

  console.debug('[getEnabledCommodities] params:', params.toString());
  const { data } = await axios.get('/Commodities/getcommodities', { params, signal });
  return data; // { items, lastId, hasMore }
}

export async function getCommodityById(id, signal = null) {
  if (id == null) throw new Error('getCommodityById: id is required');
  const { data } = await axios.get(`/Commodities/${id}`, { signal });
  return data;
}

export async function createCommodity(dto, signal = null) {
  const { data } = await axios.post('/Commodities/create', dto, { signal });
  return data;
}

export async function updateCommodity(id, dto, signal = null) {
  if (id == null) throw new Error('updateCommodity: id is required');
  await axios.put(`/Commodities/${id}`, dto, { signal });
}

export async function deleteCommodity(id, signal = null) {
  if (id == null) throw new Error('deleteCommodity: id is required');
  const { data } = await axios.delete(`/Commodities/${id}`, { signal });
  return data;
}

export async function restoreCommodity(id, signal = null) {
  if (id == null) throw new Error('restoreCommodity: id is required');
  await axios.post(`/Commodities/${id}/restore`, null, { signal });
  return true;
}

/**
 * Fetch enabled commodities by their IDs (for PPD filtering).
 * @param {number[]} ids - Array of commodity IDs to fetch
 * @returns {Promise<{items: Array}>} Promise resolving to commodities data
 */
export async function getEnabledCommoditiesByIds(ids, signal = null) {
  if (!ids || ids.length === 0) return { items: [] };
  const res = await axios.post(
    "/Commodities/byIdsEnabled",
    { filterModel: { items: [], logicOperator: 'and' } },
    {
      params: { ids },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
      signal,
    }
  );
  return res.data;
}

/**
 * Fetch lightweight commodity data by IDs (only id + display fields for weighing console).
 * Returns minimal data: id, name, erpCode, ewcCode for display purposes.
 * @param {number[]} ids - Array of commodity IDs to fetch
 * @returns {Promise<Array>} Promise resolving to array of lightweight commodity objects
 */
export async function getEnabledCommoditiesByIdsLightweight(ids, signal = null) {
  if (!ids || ids.length === 0) return [];
  const res = await axios.post(
    "/Commodities/byIdsWeighingConsole",
    ids,
    { signal }
  );
  return res.data; // Returns array directly: [{ id, name, erpCode, ewcCode }]
}
